import * as React from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Grid from "@mui/material/Grid2";
import TextField from "@mui/material/TextField";
import { FilterOptionDetail } from "./types";

export type FilterOptionFormValues = Partial<FilterOptionDetail>;

export interface FilterOptionFormState {
  values: FilterOptionFormValues;
  errors: Partial<Record<keyof FilterOptionFormValues, string>>;
}

export type FormFieldValue = string | number | boolean | undefined;

export interface ValidationResult {
  issues: { message: string; path: (keyof FilterOptionFormValues)[] }[];
}

export const validateFilterOption = (
  values: FilterOptionFormValues,
  isEdit: boolean,
): ValidationResult => {
  let issues: ValidationResult["issues"] = [];

  if (!isEdit && !values.code?.trim()) {
    issues = [...issues, { message: "Code is required", path: ["code"] }];
  }

  if (!values.description?.trim()) {
    issues = [
      ...issues,
      { message: "Description is required", path: ["description"] },
    ];
  }

  return { issues };
};

export interface FilterOptionFormProps {
  formState: FilterOptionFormState;
  isEdit: boolean;
  onFieldChange: (
    name: keyof FilterOptionFormValues,
    value: FormFieldValue,
  ) => void;
  onSubmit: () => void;
}

const FilterOptionForm = (props: FilterOptionFormProps) => {
  const { formState, isEdit, onFieldChange, onSubmit } = props;
  const formValues = formState.values;
  const formErrors = formState.errors;

  const handleTextFieldChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onFieldChange(
        event.target.name as keyof FilterOptionFormValues,
        event.target.value,
      );
    },
    [onFieldChange],
  );

  const handleNumberFieldChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      onFieldChange(
        name as keyof FilterOptionFormValues,
        value === "" ? undefined : Number(value),
      );
    },
    [onFieldChange],
  );

  const handleCheckboxFieldChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      onFieldChange(event.target.name as keyof FilterOptionFormValues, checked);
    },
    [onFieldChange],
  );

  return (
    <Box
      component="form"
      aria-label="form"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
      noValidate
      autoComplete="off"
      sx={{ width: "100%" }}
    >
      <FormGroup>
        <Grid container spacing={2} sx={{ width: "100%", pt: 1 }}>
          {!isEdit && (
            <Grid size={{ xs: 12, sm: 6 }} sx={{ display: "flex" }}>
              <TextField
                value={formValues.code ?? ""}
                onChange={handleTextFieldChange}
                name="code"
                label="Code"
                error={!!formErrors.code}
                helperText={formErrors.code ?? " "}
                fullWidth
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: isEdit ? 12 : 6 }} sx={{ display: "flex" }}>
            <TextField
              value={formValues.description ?? ""}
              onChange={handleTextFieldChange}
              name="description"
              label="Description"
              error={!!formErrors.description}
              helperText={formErrors.description ?? " "}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }} sx={{ display: "flex" }}>
            <TextField
              value={formValues.sortOrder ?? 0}
              onChange={handleNumberFieldChange}
              name="sortOrder"
              label="Sort Order"
              type="number"
              fullWidth
            />
          </Grid>
          {!isEdit && (
            <Grid size={{ xs: 12, sm: 4 }} sx={{ display: "flex" }}>
              <FormControl>
                <FormControlLabel
                  name="isSynthetic"
                  control={
                    <Checkbox
                      checked={formValues.isSynthetic ?? false}
                      onChange={handleCheckboxFieldChange}
                    />
                  }
                  label="Synthetic"
                />
              </FormControl>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 4 }} sx={{ display: "flex" }}>
            <FormControl>
              <FormControlLabel
                name="isDefaultSelected"
                control={
                  <Checkbox
                    checked={formValues.isDefaultSelected ?? false}
                    onChange={handleCheckboxFieldChange}
                  />
                }
                label="Default Selected"
              />
            </FormControl>
          </Grid>
          <Grid
            size={{ xs: 12 }}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <FormControl>
              <FormControlLabel
                name="isEnabled"
                control={
                  <Checkbox
                    checked={formValues.isEnabled ?? true}
                    onChange={handleCheckboxFieldChange}
                  />
                }
                label="Enabled"
              />
            </FormControl>
          </Grid>
        </Grid>
      </FormGroup>
    </Box>
  );
};

export default FilterOptionForm;
